import {
  generateMatterEmailDownloadUrlV2,
  type MatterEmailDownloadRequest,
} from "@/lib/data/requests/matters/matter-email";
import type { MatterEmail, MatterEmailAttachment } from "@/lib/types";
import PostalMime from "postal-mime";
import toast from "react-hot-toast";

export type MatterEmailDirection = "OUTBOUND" | "INBOUND";

const normalizeEmailAddress = (value: string) => value.trim().toLowerCase();

const emailListIncludesAddress = (
  emails: string[] | null | undefined,
  address: string,
) => {
  const normalizedAddress = normalizeEmailAddress(address);
  return (emails ?? []).some(
    (email) => normalizeEmailAddress(email) === normalizedAddress,
  );
};

export const getMatterEmailDirection = (
  email: MatterEmail,
  matterEmailAddress?: string | null,
): MatterEmailDirection => {
  const normalizedMatterAddress = matterEmailAddress?.trim();
  if (normalizedMatterAddress) {
    const recipients = [...(email.toEmails ?? []), ...(email.ccEmails ?? [])];
    if (emailListIncludesAddress(recipients, normalizedMatterAddress)) {
      return "INBOUND";
    }
    if (emailListIncludesAddress(email.fromEmails, normalizedMatterAddress)) {
      return "OUTBOUND";
    }
  }

  return "INBOUND";
};

export const isMatterEmailSent = (
  email: MatterEmail,
  matterEmailAddress?: string | null,
) => getMatterEmailDirection(email, matterEmailAddress) === "OUTBOUND";

export const isMatterEmailReceived = (
  email: MatterEmail,
  matterEmailAddress?: string | null,
) => getMatterEmailDirection(email, matterEmailAddress) === "INBOUND";

export const getMatterEmailAttachmentName = (
  attachment: MatterEmailAttachment,
) => {
  const trimmedName = attachment.name?.trim();
  if (trimmedName) {
    return trimmedName;
  }
  return "Original Email (.eml)";
};

const sanitizeDownloadFileName = (value?: string | null) => {
  const fallback = "matter-email";
  if (!value) return fallback;
  const sanitized = value
    .replace(/[\\/:*?"<>|\r\n]+/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return sanitized || fallback;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const convertPlainTextEmailToHtml = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const paragraphs = trimmed.split(/\n{2,}/);
  const htmlParagraphs = paragraphs
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => {
      const escapedParagraph = escapeHtml(paragraph);
      const withBreaks = escapedParagraph.replace(/\n/g, "<br />");
      return `<p>${withBreaks}</p>`;
    });
  if (htmlParagraphs.length) {
    return htmlParagraphs.join("");
  }
  return `<p>${escapeHtml(trimmed).replace(/\n/g, "<br />")}</p>`;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const decodeBase64ToString = (value: string) => {
  const normalized = value.replace(/\s/g, "");
  if (!normalized) return "";
  try {
    if (typeof globalThis.atob === "function") {
      const binary = globalThis.atob(normalized);
      if (typeof TextDecoder !== "undefined") {
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder("utf-8").decode(bytes);
      }
      return decodeURIComponent(
        binary
          .split("")
          .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
          .join(""),
      );
    }
  } catch (error) {
    console.error("Unable to decode base64 email content", error);
  }
  return normalized;
};

const decodeQuotedPrintable = (value: string) => {
  return value
    .replace(/=\r?\n/g, "")
    .replace(/=([0-9A-F]{2})/gi, (_, hex) =>
      String.fromCharCode(parseInt(hex as string, 16)),
    );
};

const maybeDecodeQuotedPrintable = (value?: string | null) => {
  if (!value) return value ?? undefined;
  const hasQuotedPrintable =
    /=\r?\n/.test(value) ||
    /=3D/i.test(value) ||
    /=0[A-F]/i.test(value) ||
    /=20/i.test(value) ||
    /=E[0-9A-F]/i.test(value) ||
    /=C[0-9A-F]/i.test(value);
  if (!hasQuotedPrintable) {
    return value;
  }
  try {
    return decodeQuotedPrintable(value);
  } catch (error) {
    console.error("Unable to decode quoted-printable content", error);
  }
  return value;
};

const findBoundaryNearIndex = (eml: string, index: number) => {
  const substring = eml.slice(0, index);
  const boundaryRegex = /boundary=\"?([^\";\r\n]+)\"?/gi;
  let match: RegExpExecArray | null;
  let boundary: string | undefined;
  while ((match = boundaryRegex.exec(substring))) {
    boundary = match[1];
  }
  return boundary;
};

const decodeEmlBody = (body: string, headers: string) => {
  const trimmedBody = body.trim();
  if (!trimmedBody) return "";
  const encodingMatch = headers.match(
    /Content-Transfer-Encoding:\s*([^\r\n]+)/i,
  );
  const encoding = encodingMatch?.[1]?.trim().toLowerCase();
  if (encoding === "base64") {
    return decodeBase64ToString(trimmedBody);
  }
  if (encoding === "quoted-printable") {
    return decodeQuotedPrintable(trimmedBody);
  }
  return trimmedBody;
};

const extractEmlPartContent = (eml: string, contentType: string) => {
  const typePattern = contentType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const headerRegex = new RegExp(
    `Content-Type:\\s*${typePattern}[^\\r\\n]*(?:\\r?\\n[^\\r\\n]+)*\\r?\\n\\r?\\n`,
    "i",
  );
  const match = headerRegex.exec(eml);
  if (!match || typeof match.index === "undefined") {
    return undefined;
  }
  const headerStart = match.index;
  const headerEnd = headerStart + match[0].length;
  const headers = eml.slice(headerStart, headerEnd);
  const boundary = findBoundaryNearIndex(eml, headerStart);
  const following = eml.slice(headerEnd);
  const boundaryRegex = boundary
    ? new RegExp(`\\r?\\n--${escapeRegExp(boundary)}(?:--)?`)
    : /\r?\n--[^\r\n]+/;
  const boundaryMatch = boundaryRegex.exec(following);
  const rawBody = boundaryMatch
    ? following.slice(0, boundaryMatch.index)
    : following;
  const content = decodeEmlBody(rawBody, headers).trim();
  if (!content) {
    return undefined;
  }
  return content;
};

export interface ParsedMatterEmailPreview {
  html?: string;
  text?: string;
}

const decodeArrayBufferToString = (value: ArrayBuffer) => {
  try {
    return new TextDecoder("utf-8").decode(value);
  } catch (error) {
    console.error("Unable to decode ArrayBuffer preview content", error);
    return "";
  }
};

const parseEmlPreviewFallback = (eml?: string | null) => {
  if (!eml) return undefined;
  const trimmed = eml.trim();
  if (!trimmed) return undefined;
  const html = extractEmlPartContent(trimmed, "text/html");
  const text = extractEmlPartContent(trimmed, "text/plain");
  if (html || text) {
    const normalizedHtml = maybeDecodeQuotedPrintable(html);
    const normalizedText = maybeDecodeQuotedPrintable(text);
    if (normalizedHtml || normalizedText) {
      return {
        ...(normalizedHtml ? { html: normalizedHtml } : {}),
        ...(normalizedText ? { text: normalizedText } : {}),
      };
    }
  }
  return { text: trimmed };
};

export const parseEmlPreviewContent = async (
  eml?: ArrayBuffer | string | null,
): Promise<ParsedMatterEmailPreview | undefined> => {
  if (!eml) return undefined;
  const parser = new PostalMime();
  try {
    const parsed = await parser.parse(
      typeof eml === "string" ? eml : new Uint8Array(eml),
    );
    if (parsed.html || parsed.text) {
      const normalizedHtml = maybeDecodeQuotedPrintable(parsed.html);
      const normalizedText = maybeDecodeQuotedPrintable(parsed.text);
      if (normalizedHtml || normalizedText) {
        return {
          ...(normalizedHtml ? { html: normalizedHtml } : {}),
          ...(normalizedText ? { text: normalizedText } : {}),
        };
      }
    }
  } catch (error) {
    console.error("Unable to parse EML content with postal-mime", error);
  }
  if (typeof eml === "string") {
    return parseEmlPreviewFallback(eml);
  }
  return parseEmlPreviewFallback(decodeArrayBufferToString(eml));
};

export const downloadMatterEmailAsset = async ({
  emailId,
  attachmentId,
  fileName,
}: MatterEmailDownloadRequest & { fileName: string }) => {
  if (!emailId) return;
  if (typeof window === "undefined") return;

  try {
    const presignedRes = await generateMatterEmailDownloadUrlV2({
      emailId,
      attachmentId,
    });
    const safeFileName = sanitizeDownloadFileName(fileName);
    const downloadUrl = presignedRes.data.presignedUrl;
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.rel = "noopener noreferrer";
    anchor.target = "_blank";
    anchor.download = safeFileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } catch (error) {
    console.error("Error downloading matter email asset", error);
    toast.error("Unable to download file. Please try again.");
  }
};
